// src/lib/tutor-tools/plan-check-layout.ts
//
// How one assistant reply lays out its check_plan_time calls.
//
// QA: asked for a plan with a daily budget, Sage drafts, checks, finds an
// activity that doesn't fit, redrafts and checks again — all in one reply (the
// route stores every round's tool calls on the same message). The chat then
// showed every failed check next to the finished plan, so the reply read as
// if the plan still didn't fit. The rule:
//
//   - The FINAL plan check is the last check_plan_time call of the reply. If
//     it fits, it's the answer: it renders as the prominent "Fits your …"
//     card, next to the plan text.
//   - A check that didn't fit (or errored) is SUPERSEDED when a LATER check in
//     the same reply fits. Superseded checks collapse into an optional
//     "N earlier drafts didn't fit" disclosure.
//   - A failed check with no passing check after it is the live answer (the
//     plan really doesn't fit, or Sage is offering an exceptional session):
//     it stays visible, as before...
//   - ...UNLESS the reply moved on to something to start instead (it carries
//     a Start card). QA: asked for one easy question within ten minutes, Sage
//     checked a combined session, it didn't fit, and Sage offered a smaller
//     activity — but the failed check still sat under the reply as if it were
//     the plan. Such a check is SET ASIDE: collapsed under "Didn't fit —
//     options below", the Start card being the option below it.
//   - A check still running is neither: it stays where it is.
//
// Pure and client-safe — TutorChatPanel renders from it, for a live stream
// and a history reload alike (both carry the same toolCalls/toolResults).

export const PLAN_CHECK_TOOL = 'check_plan_time'

type CallLike = { id: string; name: string }
type ResultLike = { ok: boolean; result?: unknown }

export type PlanCheckOutcome = 'fits' | 'misses' | 'running'

/**
 * Whether a finished check_plan_time result says the plan fits. Trusts
 * `allFit` when the result carries it; otherwise reads the activities and
 * sessions, so an older stored row is judged the same way.
 */
export function planCheckFits(result: unknown): boolean {
  if (!result || typeof result !== 'object') return false
  const r = result as {
    allFit?: unknown
    activities?: Array<{ fits?: unknown }>
    overBudgetSessions?: unknown[]
  }
  if (typeof r.allFit === 'boolean') return r.allFit
  if (!Array.isArray(r.activities) || r.activities.length === 0) return false
  const over = Array.isArray(r.overBudgetSessions) ? r.overBudgetSessions : []
  return r.activities.every((a) => a?.fits === true) && over.length === 0
}

/** The outcome of a plan check, or null for any other tool. */
export function planCheckOutcome(
  call: CallLike,
  result: ResultLike | undefined,
): PlanCheckOutcome | null {
  if (call.name !== PLAN_CHECK_TOOL) return null
  if (!result) return 'running'
  return result.ok && planCheckFits(result.result) ? 'fits' : 'misses'
}

export type PlanCheckLayout<C> = {
  /** The reply's final plan check, when it fits — the prominent card. */
  finalFit: C | null
  /** Failed checks a later passing check replaced, in order. */
  superseded: C[]
  /**
   * Failed checks with no passing check after them, in a reply that offers a
   * Start card instead (opts.hasOffer). Empty when the reply has no card —
   * then a failed final check is the live answer and stays in `rest`.
   */
  setAside: C[]
  /** Everything else, in the order given. */
  rest: C[]
}

export function layoutPlanChecks<C extends CallLike>(
  calls: readonly C[],
  resultFor: (call: C) => ResultLike | undefined,
  /** hasOffer: the reply carries a Start card (offer_activity / a legacy drill). */
  opts: { hasOffer?: boolean } = {},
): PlanCheckLayout<C> {
  const outcomes = calls.map((c) => planCheckOutcome(c, resultFor(c)))

  let lastCheck = -1
  let lastFit = -1
  outcomes.forEach((o, i) => {
    if (o === null) return
    lastCheck = i
    if (o === 'fits') lastFit = i
  })

  const finalIndex = lastCheck !== -1 && outcomes[lastCheck] === 'fits' ? lastCheck : -1
  const finalFit = finalIndex === -1 ? null : calls[finalIndex]
  const superseded: C[] = []
  const setAside: C[] = []
  const rest: C[] = []
  calls.forEach((c, i) => {
    if (i === finalIndex) return
    if (outcomes[i] === 'misses' && i < lastFit) superseded.push(c)
    else if (outcomes[i] === 'misses' && finalIndex === -1 && opts.hasOffer) setAside.push(c)
    else rest.push(c)
  })
  return { finalFit, superseded, setAside, rest }
}

/**
 * The set-aside disclosure's label. The Start card renders after the checks,
 * so "below" points at it. An errored check isn't a misfit, so a set
 * containing one says it couldn't be checked instead.
 */
export function setAsideLabel<C extends CallLike>(
  setAside: readonly C[],
  resultFor: (call: C) => ResultLike | undefined,
): string {
  const allMisfits = setAside.every((c) => resultFor(c)?.ok === true)
  if (!allMisfits) return "Couldn't check that plan — options below"
  return setAside.length === 1
    ? "Didn't fit — options below"
    : `${setAside.length} checks didn't fit — options below`
}

/**
 * The disclosure's label: "2 earlier drafts didn't fit". A superseded check
 * that errored (bad input, not a misfit) isn't a draft that didn't fit, so a
 * set containing one is labelled as earlier checks instead.
 */
export function supersededLabel<C extends CallLike>(
  superseded: readonly C[],
  resultFor: (call: C) => ResultLike | undefined,
): string {
  const n = superseded.length
  const allMisfits = superseded.every((c) => resultFor(c)?.ok === true)
  return allMisfits
    ? `${n} earlier ${n === 1 ? "draft didn't" : "drafts didn't"} fit`
    : `${n} earlier ${n === 1 ? 'check' : 'checks'}`
}
